import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { HelpdeskFilters } from './HelpdeskAdmin/HelpdeskFilters';
import { HelpdeskStats } from './HelpdeskAdmin/HelpdeskStats';
import { HelpdeskTable } from './HelpdeskAdmin/HelpdeskTable';
import { HelpdeskTicketDialog } from './HelpdeskAdmin/HelpdeskTicketDialog';
import { useHelpdeskData } from './HelpdeskAdmin/useHelpdeskData';

const HelpdeskAdminPanel: React.FC = () => {
  const theme = useTheme();
  const { hasPermission } = useAccount();
  const canViewTickets = hasPermission(PERMISSIONS.TICKET_VIEW);
  const canUpdateTickets = hasPermission(PERMISSIONS.TICKET_UPDATE);

  const {
    loading,
    stats,
    activeTab,
    filters,
    filteredTickets,
    expandedRows,
    openDialog,
    selectedTicket,
    responseForm,
    statusForm,
    statusReason,

    setOpenDialog,
    loadTickets,
    handleTicketClick,
    toggleRowExpansion,
    handleTabChange,
    handleFilterChange,
    clearFilters,
    handleResponseFormChange,
    handleStatusFormChange,
    handleStatusReasonChange,
    handleAddResponse,
    handleUpdateStatus,
    handleQuickResolve,
    handleQuickClose,
  } = useHelpdeskData();

  if (!canViewTickets) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Access Denied
        </Typography>
        <Typography>You don't have permission to view support tickets.</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontSize: '1.1rem' }}>
          Helpdesk Admin
        </Typography>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={loadTickets}
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.bidlogiq.brick,
            color: theme.palette.common.white,
            fontSize: '1.1rem',
            '&:hover': { backgroundColor: theme.palette.bidlogiq.rust },
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      <HelpdeskStats stats={stats} />

      <HelpdeskFilters
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        filters={filters}
        handleFilterChange={handleFilterChange}
        clearFilters={clearFilters}
      />

      <HelpdeskTable
        filteredTickets={filteredTickets}
        loading={loading}
        expandedRows={expandedRows}
        toggleRowExpansion={toggleRowExpansion}
        handleTicketClick={handleTicketClick}
      />

      <HelpdeskTicketDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedTicket={selectedTicket}
        canUpdateTickets={canUpdateTickets}
        statusForm={statusForm}
        handleStatusFormChange={handleStatusFormChange}
        statusReason={statusReason}
        handleStatusReasonChange={handleStatusReasonChange}
        handleUpdateStatus={handleUpdateStatus}
        handleQuickResolve={handleQuickResolve}
        handleQuickClose={handleQuickClose}
        responseForm={responseForm}
        handleResponseFormChange={handleResponseFormChange}
        handleAddResponse={handleAddResponse}
      />
    </Box>
  );
};

export default HelpdeskAdminPanel;
